import React, { useState, useContext, useRef, useEffect } from 'react';
import './Login.styles.scss';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import googleIcon from '../../../../src/assets/svg/googleIcon.svg';
import { checkSignupFormValidation, hasErrorInSIgnupForm } from '../../../utils/formValidations';
import { FirebaseAuthContext } from '../../../auth/FirebaseAuth';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../components/Logo/Logo.component';
import { showToastr } from '../../../components/ToastrMessage/Toast.helper';
import { APP_LABEL } from '../../../translations';
import { useDispatch } from 'react-redux';
import { setLoggedInUserInfo, setUserDisplayName, setUserLogInState } from '../../../store/user/userSlice';
import useTranslation from '../../../translations/use-translation';

const Login = ({ setOpen, setIsUserLoggedIn, setUserInfo, setDisplayName, onBack, redirectTo = '/' }) => {
  const translate = useTranslation();
  const [isSignInForm, setIsSignInForm] = useState(true);
  const [isForgotPasswordForm, setIsForgotPasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isResetSuccess, setIsResetSuccess] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  // ── Mobile login state ──
  const [loginMethod, setLoginMethod] = useState('email');
  const [mobileNumber, setMobileNumber] = useState('');
  const [otpDigits, setOtpDigits] = useState(Array(6).fill(''));
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [mobileError, setMobileError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);

  const otpInputRefs = useRef([]);
  const recaptchaVerifierRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { signUpUser, signInUser, resetPassword, updateUserProfile, signInWithGooglePopup, setupRecaptcha, sendPhoneOtp } =
    useContext(FirebaseAuthContext);

  const [userDetails, setUserDetails] = useState({ username: '', email: '', password: '', rePassword: '' });
  const [userDetailsError, setUserDetailsError] = useState({ username: '', email: '', password: '' });

  // ── OTP countdown timer ──
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Auto-focus first OTP input when OTP screen appears
  useEffect(() => {
    if (otpSent) {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 50);
    }
  }, [otpSent]);

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  // ── Existing handlers ──
  const handleFormChange = (e) =>
    setUserDetails((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleClickShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const setUserInfoToStore = (authState, userName, userInfo) => {
    localStorage.setItem('userProfile', JSON.stringify(userInfo));
    localStorage.setItem('userDisplayName', JSON.stringify(userName));
    localStorage.setItem('isUserLoggedIn', authState);
    setIsUserLoggedIn(authState);
    setUserInfo(userInfo);
    setDisplayName(userName);
    dispatch(setUserLogInState(authState));
    dispatch(setLoggedInUserInfo(authState, userInfo));
    dispatch(setUserDisplayName(userName));
  };

  const resetLoginFormError = () => {
    setUserDetailsError({ username: '', email: '', password: '', rePassword: '' });
    setUserDetails((prev) => ({ ...prev, username: '', password: '', rePassword: '' }));
  };

  const switchToTab = (signIn) => {
    setIsSignInForm(signIn);
    resetLoginFormError();
  };

  const handleUserAuthentication = async () => {
    const errorMessage = checkSignupFormValidation(userDetails);

    if (!isSignInForm && !isForgotPasswordForm) {
      setUserDetailsError(errorMessage);
      if (!hasErrorInSIgnupForm(errorMessage)) {
        setShowLoader(true);
        await signUpUser(userDetails.email, userDetails.password)
          .then((data) => {
            updateUserProfile(userDetails.username).then(() => {
              setUserInfoToStore(true, data?.user?.displayName, data?.user);
              setShowLoader(false);
            });
            setOpen(false);
          })
          .catch((error) => {
            setShowLoader(false);
            if (error.code === 'auth/email-already-in-use')
              setUserDetailsError({ email: 'Email already in use, try with another email' });
            if (error.code.includes('password'))
              setUserDetailsError({ password: error.message });
            if (error.code === 'auth/network-request-failed')
              showToastr('error', APP_LABEL['en'].ERROR_NO_INTERNET_CONNECTION);
            setOpen(true);
          });
      }
    } else if (isSignInForm && !isForgotPasswordForm) {
      setUserDetailsError({
        ...errorMessage,
        password: !userDetails.password ? 'Password is required' : '',
      });
      if (errorMessage.email || !userDetails.password) return;
      setShowLoader(true);
      signInUser(userDetails.email, userDetails.password)
        .then((data) => {
          setShowLoader(false);
          setUserInfoToStore(true, data?.user?.displayName, data?.user);
          setOpen(false);
          navigate(redirectTo);
        })
        .catch((error) => {
          setShowLoader(false);
          if (error.code === 'auth/invalid-login-credentials')
            setUserDetailsError({ email: 'Invalid login details' });
          if (error.code === 'auth/network-request-failed')
            showToastr('error', APP_LABEL['en'].ERROR_NO_INTERNET_CONNECTION);
          if (error?.code === 'auth/too-many-requests')
            showToastr('error', APP_LABEL['en'].ERROR_ACCOUNT_DISABLED);
        });
    } else {
      setUserDetailsError(errorMessage);
      if (errorMessage.email) return;
      setShowLoader(true);
      resetPassword(userDetails.email)
        .then(() => {
          resetLoginFormError();
          setIsResetSuccess(true);
          setShowLoader(false);
        })
        .catch((error) => {
          setShowLoader(false);
          if (error.code === 'auth/network-request-failed')
            showToastr('error', APP_LABEL['en'].ERROR_NO_INTERNET_CONNECTION);
        });
    }
  };

  const signInWithGoogle = () => {
    resetLoginFormError();
    signInWithGooglePopup()
      .then((data) => {
        setUserInfoToStore(true, data?.user?.displayName, data?.user);
        setOpen(false);
      })
      .catch((error) => console.log(error));
  };

  const getSubmitLabel = () => {
    if (isSignInForm) return showLoader ? translate('LOGIN_LOADING') : translate('LOGIN_BUTTON');
    if (!isForgotPasswordForm) return showLoader ? translate('SIGNUP_LOADING') : translate('SIGNUP_BUTTON');
    return showLoader ? translate('RESET_PASSWORD_LOADING') : translate('RESET_PASSWORD_BUTTON');
  };

  // ── Firebase phone auth error helper ──
  const getPhoneAuthErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-phone-number':
        return 'Invalid phone number';
      case 'auth/too-many-requests':
        return 'Too many attempts. Try again later.';
      case 'auth/network-request-failed':
        return 'No internet connection';
      case 'auth/invalid-verification-code':
        return 'Invalid OTP. Please try again.';
      case 'auth/code-expired':
        return 'OTP expired. Please resend.';
      default:
        return 'Something went wrong. Try again.';
    }
  };

  const clearRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
  };

  // ── Mobile login handlers ──
  const handleSendOtp = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      setMobileError('Enter a valid 10-digit mobile number');
      return;
    }
    setMobileError('');
    setShowLoader(true);
    try {
      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = setupRecaptcha('recaptcha-container');
      }
      const result = await sendPhoneOtp('+91' + mobileNumber, recaptchaVerifierRef.current);
      setConfirmationResult(result);
      setOtpSent(true);
      setOtpTimer(30);
    } catch (error) {
      setMobileError(getPhoneAuthErrorMessage(error.code));
      clearRecaptcha();
    } finally {
      setShowLoader(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpString = otpDigits.join('');
    if (!/^\d{6}$/.test(otpString)) {
      setOtpError('Enter a valid 6-digit OTP');
      return;
    }
    setOtpError('');
    setShowLoader(true);
    try {
      const data = await confirmationResult.confirm(otpString);
      setUserInfoToStore(true, data?.user?.displayName || data?.user?.phoneNumber, data?.user);
      setOpen(false);
      navigate(redirectTo);
    } catch (error) {
      setOtpError(getPhoneAuthErrorMessage(error.code));
    } finally {
      setShowLoader(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpTimer > 0) return;
    setOtpDigits(Array(6).fill(''));
    setOtpError('');
    setShowLoader(true);
    try {
      clearRecaptcha();
      recaptchaVerifierRef.current = setupRecaptcha('recaptcha-container');
      const result = await sendPhoneOtp('+91' + mobileNumber, recaptchaVerifierRef.current);
      setConfirmationResult(result);
      setOtpTimer(30);
    } catch (error) {
      setOtpError(getPhoneAuthErrorMessage(error.code));
      clearRecaptcha();
    } finally {
      setShowLoader(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setOtpError('');
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const newDigits = [...otpDigits];
      newDigits[index - 1] = '';
      setOtpDigits(newDigits);
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newDigits = Array(6).fill('');
    pasted.split('').forEach((d, i) => { newDigits[i] = d; });
    setOtpDigits(newDigits);
    setOtpError('');
    const focusIndex = Math.min(pasted.length, 5);
    otpInputRefs.current[focusIndex]?.focus();
  };

  const switchToEmail = () => {
    setLoginMethod('email');
    setOtpSent(false);
    setOtpDigits(Array(6).fill(''));
    setOtpTimer(0);
    setMobileError('');
    setOtpError('');
    setConfirmationResult(null);
    clearRecaptcha();
    resetLoginFormError();
  };

  const switchToMobile = () => {
    setLoginMethod('mobile');
    setIsForgotPasswordForm(false);
    setIsResetSuccess(false);
    resetLoginFormError();
  };

  return (
    <div className="login-container">

      {/* ── Close ── */}
      <button
        className="login-close-btn"
        onClick={() => setOpen(false)}
        aria-label="Close"
        type="button"
      >
        <CloseIcon fontSize="small" />
      </button>

      {/* ── Back to account type selection ── */}
      {onBack && (
        <button
          type="button"
          className="login-back-to-select"
          onClick={onBack}
          aria-label="Back to account type selection"
        >
          ← Back
        </button>
      )}

      {loginMethod === 'mobile' && !isForgotPasswordForm ? (
        /* ═══════════════════════════════════════════════════════
           MOBILE LOGIN (default)
           ═══════════════════════════════════════════════════════ */
        <>
          {/* ── Brand header ── */}
          <div className="login-header">
            <div className="login-brand-row">
              <Logo width="28px" />
              <span className="login-brand-name">{translate('ONLINE_APPLICATIONS')}</span>
            </div>
            <h2 className="login-title">
              {otpSent ? 'Verify OTP' : 'Welcome back'}
            </h2>
            <p className="login-subtitle">
              {otpSent
                ? `OTP sent to +91 ${mobileNumber}`
                : 'Sign in with your mobile number'}
            </p>
          </div>

          {!otpSent ? (
            /* ── Phone input screen ── */
            <form
              className="login-form"
              onSubmit={(e) => { e.preventDefault(); handleSendOtp(); }}
              noValidate
            >
              <TextField
                fullWidth
                size="small"
                label="Mobile Number"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setMobileError('');
                }}
                error={!!mobileError}
                helperText={mobileError}
                variant="outlined"
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span className="login-phone-prefix">&#127470;&#127475; +91</span>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                type="submit"
                className="login-submit-btn"
                variant="contained"
                color="primary"
                disableElevation
                disabled={showLoader}
                endIcon={showLoader && <CircularProgress color="inherit" size={16} />}
              >
                {showLoader ? 'Sending...' : 'Send OTP'}
              </Button>

              <div className="login-divider"><span>or</span></div>

              <button
                type="button"
                className="login-method-toggle"
                onClick={switchToEmail}
              >
                Use email instead
              </button>
            </form>
          ) : (
            /* ── OTP verification screen ── */
            <form
              className="login-form"
              onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }}
              noValidate
            >
              <div className="login-otp-row">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => { otpInputRefs.current[i] = el; }}
                    className={`login-otp-input${otpError ? ' login-otp-input--error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otpDigits[i]}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={handleOtpPaste}
                  />
                ))}
              </div>
              {otpError && <p className="login-otp-error">{otpError}</p>}

              <Button
                fullWidth
                type="submit"
                className="login-submit-btn"
                variant="contained"
                color="primary"
                disableElevation
                disabled={showLoader}
                endIcon={showLoader && <CircularProgress color="inherit" size={16} />}
              >
                {showLoader ? 'Verifying...' : 'Verify OTP'}
              </Button>

              <div className="login-resend-row">
                {otpTimer > 0 ? (
                  <span className="login-resend-timer">
                    Resend OTP in 0:{otpTimer.toString().padStart(2, '0')}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="login-resend-link"
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                  </button>
                )}
              </div>

              <div className="login-divider"><span>or</span></div>

              <button
                type="button"
                className="login-method-toggle"
                onClick={() => {
                  setOtpSent(false);
                  setOtpDigits(Array(6).fill(''));
                  setOtpError('');
                  clearRecaptcha();
                }}
              >
                &#8592; Change number
              </button>
            </form>
          )}
        </>
      ) : (
        /* ═══════════════════════════════════════════════════════
           EMAIL LOGIN (existing)
           ═══════════════════════════════════════════════════════ */
        <>
          {/* ── Brand header ── */}
          <div className="login-header">
            <div className="login-brand-row">
              <Logo width="28px" />
              <span className="login-brand-name">{translate('ONLINE_APPLICATIONS')}</span>
            </div>

            <h2 className="login-title">
              {isForgotPasswordForm
                ? (isResetSuccess ? 'Check your inbox' : 'Reset password')
                : (isSignInForm ? 'Welcome back' : 'Create account')}
            </h2>
            <p className="login-subtitle">
              {isForgotPasswordForm
                ? (isResetSuccess
                    ? `We sent a reset link to ${userDetails.email}`
                    : 'Enter your email to receive a reset link')
                : (isSignInForm
                    ? 'Sign in to continue to your account'
                    : 'Register to get started')}
            </p>
          </div>

          {/* ── Tab switcher (login / sign-up only) ── */}
          {!isForgotPasswordForm && (
            <div className="login-tab-row" role="tablist">
              <button
                role="tab"
                type="button"
                aria-selected={isSignInForm}
                className={`login-tab${isSignInForm ? ' login-tab--active' : ''}`}
                onClick={() => switchToTab(true)}
              >
                {translate('LOGIN_BUTTON')}
              </button>
              <button
                role="tab"
                type="button"
                aria-selected={!isSignInForm}
                className={`login-tab${!isSignInForm ? ' login-tab--active' : ''}`}
                onClick={() => switchToTab(false)}
              >
                {translate('SIGNUP_BUTTON')}
              </button>
            </div>
          )}

          {/* ── Form ── */}
          <form
            className="login-form"
            onSubmit={(e) => { e.preventDefault(); handleUserAuthentication(); }}
            noValidate
          >
            {/* Full name — sign-up only */}
            {!isSignInForm && !isForgotPasswordForm && (
              <TextField
                size="small"
                id="name"
                name="username"
                required
                fullWidth
                error={!!userDetailsError.username}
                helperText={userDetailsError.username}
                label={translate('FULLNAME_LABEL')}
                value={userDetails.username}
                onChange={handleFormChange}
                variant="outlined"
              />
            )}

            {/* Reset success state */}
            {isResetSuccess ? (
              <div className="login-reset-success">
                <CheckCircleOutlineIcon />
                <p>{translate('RESET_PASSWORD_SUCCESS').replace('{email}', userDetails.email)}</p>
              </div>
            ) : (
              <TextField
                fullWidth
                size="small"
                id="email"
                name="email"
                label={translate('EMAIL_LABEL')}
                required
                error={!!userDetailsError.email}
                helperText={userDetailsError.email}
                value={userDetails.email}
                onChange={handleFormChange}
                variant="outlined"
                type="email"
              />
            )}

            {/* Password */}
            {!isForgotPasswordForm && (
              <TextField
                fullWidth
                size="small"
                id="password"
                name="password"
                label={translate('PASSWORD_LABEL')}
                required
                error={!!userDetailsError.password}
                helperText={userDetailsError.password}
                type={showPassword ? 'text' : 'password'}
                value={userDetails.password}
                onChange={handleFormChange}
                autoComplete="current-password"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        size="small"
                        edge="end"
                      >
                        {showPassword
                          ? <VisibilityOff fontSize="small" />
                          : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {/* Forgot password link — sign-in mode only */}
            {isSignInForm && !isForgotPasswordForm && (
              <button
                type="button"
                className="login-forgot-link"
                onClick={() => {
                  setIsForgotPasswordForm(true);
                  setIsSignInForm(false);
                  resetLoginFormError();
                }}
              >
                {translate('RESET_PASSWORD_LINK')}
              </button>
            )}

            {/* Submit */}
            {!isResetSuccess && (
              <Button
                fullWidth
                type="submit"
                className="login-submit-btn"
                variant="contained"
                color="primary"
                disableElevation
                endIcon={showLoader && <CircularProgress color="inherit" size={16} />}
              >
                {getSubmitLabel()}
              </Button>
            )}

            {/* Back / Cancel — forgot password flow */}
            {isForgotPasswordForm && (
              <button
                type="button"
                className="login-back-link"
                onClick={() => {
                  setIsForgotPasswordForm(false);
                  setIsSignInForm(true);
                  resetLoginFormError();
                  setIsResetSuccess(false);
                }}
              >
                ← {isResetSuccess ? translate('BACK_TO_LOGIN') : translate('CANCEL')}
              </button>
            )}

            {/* OR divider + Google — sign-in mode only */}
            {!isForgotPasswordForm && isSignInForm && (
              <>
                <div className="login-divider">
                  <span>{translate('OR')}</span>
                </div>
                <Button
                  fullWidth
                  type="button"
                  variant="outlined"
                  className="login-google-btn"
                  onClick={signInWithGoogle}
                  startIcon={
                    <img src={googleIcon} alt="google" width={18} height={18} />
                  }
                >
                  {translate('LOGIN_WITH_GOOGLE')}
                </Button>
              </>
            )}

            {/* "Use mobile instead" link */}
            {!isForgotPasswordForm && (
              <>
                <div className="login-divider"><span>or</span></div>
                <button
                  type="button"
                  className="login-method-toggle"
                  onClick={switchToMobile}
                >
                  Use mobile instead
                </button>
              </>
            )}
          </form>
        </>
      )}
      {/* Hidden reCAPTCHA container for Firebase phone auth */}
      <div id="recaptcha-container"></div>
    </div>
  );
};

export default Login;
